/* prettier-ignore-file */
/**
 * Surfaces booking states that need a human, at the top of the bookings list.
 *
 * Two of these states can now exist and neither was visible anywhere:
 *
 *  - `oversold: true` — set by the Stripe webhook when a booking was confirmed for
 *    items that were no longer available. The webhook deliberately confirms anyway
 *    (the card is already charged), so the ONLY way anyone learns about it is here.
 *  - `status: "awaiting_cash"` — a booking held at the register. If staff walk away
 *    mid-transaction it sits in pendingBookings indefinitely, holding inventory for
 *    the first 15 minutes and then quietly holding nothing.
 *
 * A flag nobody sees is barely better than no flag, which is why this exists.
 */

import React from "react";
import { View, Text, StyleSheet, TouchableOpacity } from "react-native";
import { useNavigate } from "react-router-dom";
import { useBookings, usePendingBookings } from "../stores";
import { capitalizeWords, formatCentsAsUSD, parseFirestoreTimestamp } from "../utils/formatting";
import { HOLD_WINDOW_MS } from "../utils/rentalAvailability";
import { colors } from "../theme";

const nameOf = (booking) =>
  capitalizeWords(`${booking?.firstName || ""} ${booking?.lastName || ""}`.trim()) || "Unknown";

/** "just now" / "12m ago" / "3h ago" — enough to judge whether a hold is abandoned. */
const ageLabel = (millis) => {
  if (!millis) return "";
  const mins = Math.floor((Date.now() - millis) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

const BookingAlertsStrip = () => {
  const navigate = useNavigate();
  const bookings = useBookings((state) => state.bookings) || [];
  const pendingBookings = usePendingBookings((state) => state.pendingBookings) || [];

  const oversold = bookings.filter((b) => b?.oversold === true && b?.cancelled !== true);
  const awaitingCash = pendingBookings.filter((b) => b?.status === "awaiting_cash");

  if (oversold.length === 0 && awaitingCash.length === 0) return null;

  return (
    <View style={styles.container}>
      {oversold.length > 0 && (
        <View style={[styles.card, styles.oversoldCard]}>
          <Text style={styles.oversoldTitle}>
            🚨 {oversold.length} oversold {oversold.length === 1 ? "booking" : "bookings"}
          </Text>
          <Text style={styles.body}>
            Confirmed and paid, but the items were already taken. Payment was captured, so
            these need resolving with the customer.
          </Text>
          {oversold.slice(0, 4).map((booking) => {
            const id = booking.id || booking.bookingId;
            return (
              <TouchableOpacity
                key={id}
                dataSet={{ hover: "lift" }}
                style={styles.row}
                onPress={() => navigate(`/view-booking/${id}`)}
              >
                <View style={styles.rowText}>
                  <Text style={styles.rowName}>{nameOf(booking)}</Text>
                  <Text style={styles.rowMeta}>
                    {(booking.oversoldItems || [])
                      .map((i) => `${i.name} (need ${i.wanted}, ${i.available} free)`)
                      .join(" · ") || "See booking for details"}
                  </Text>
                </View>
                <Text style={styles.chevron}>›</Text>
              </TouchableOpacity>
            );
          })}
          {oversold.length > 4 && (
            <Text style={styles.more}>+{oversold.length - 4} more</Text>
          )}
        </View>
      )}

      {awaitingCash.length > 0 && (
        <View style={[styles.card, styles.cashCard]}>
          <Text style={styles.cashTitle}>
            ⏳ {awaitingCash.length} awaiting cash payment
          </Text>
          <Text style={styles.body}>
            Waiver signed, held for payment at the register. Not booked until confirmed.
          </Text>
          {awaitingCash.slice(0, 4).map((booking) => {
            const id = booking.id || booking.bookingId;
            const created = parseFirestoreTimestamp(booking.createdAt);
            // Past the hold window it no longer reserves anything, so the items can be
            // sold out from under it — worth calling out rather than just showing an age.
            const stale = created && Date.now() - created > HOLD_WINDOW_MS;
            return (
              <TouchableOpacity
                key={id}
                dataSet={{ hover: "lift" }}
                style={styles.row}
                onPress={() => navigate(`/cash-payment/${id}`)}
              >
                <View style={styles.rowText}>
                  <Text style={styles.rowName}>{nameOf(booking)}</Text>
                  <Text style={styles.rowMeta}>
                    {formatCentsAsUSD(booking.totalAmount || 0)} · started {ageLabel(created)}
                    {stale ? " · no longer holding inventory" : ""}
                  </Text>
                </View>
                <Text style={styles.chevron}>›</Text>
              </TouchableOpacity>
            );
          })}
          {awaitingCash.length > 4 && <Text style={styles.more}>+{awaitingCash.length - 4} more</Text>}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { marginBottom: 12 },
  card: { borderRadius: 8, padding: 14, marginBottom: 10, borderLeftWidth: 4 },
  oversoldCard: { backgroundColor: colors.dangerSoft, borderLeftColor: colors.danger },
  cashCard: { backgroundColor: colors.warningSoft, borderLeftColor: colors.warning },
  oversoldTitle: { fontSize: 15, fontWeight: "700", color: colors.danger, marginBottom: 4 },
  cashTitle: { fontSize: 15, fontWeight: "700", color: colors.warningInk, marginBottom: 4 },
  body: { fontSize: 12, color: colors.ink700, lineHeight: 17, marginBottom: 8 },
  row: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: colors.surface,
    borderRadius: 6,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginTop: 6,
    minHeight: 48,
    cursor: "pointer",
  },
  rowText: { flex: 1, paddingRight: 8 },
  rowName: { fontSize: 14, fontWeight: "600", color: colors.ink900 },
  rowMeta: { fontSize: 12, color: colors.ink500, marginTop: 2 },
  chevron: { fontSize: 20, color: colors.ink400 },
  more: { fontSize: 12, color: colors.ink500, marginTop: 8, textAlign: "center" },
});

export default BookingAlertsStrip;
